import { createSlice } from '@reduxjs/toolkit'
import { TransformationProfile } from 'common/api/v1/types'
import { logoutUser } from '../actions/userActions'
import { createLoadingReducer } from './shared'
import {
  listTransformationProfiles,
  getTransformationProfile,
  createTransformationProfile,
  updateTransformationProfile,
  deleteTransformationProfile,
  clearTransformationProfileErrors,
} from '../actions/transformationProfileActions'
import type { ApplicationException } from '../../api/ApplicationException'

interface State {
  transformationProfiles: Array<TransformationProfile>
  selectedTransformationProfile?: TransformationProfile
  total: number
  loading: boolean
  saving: boolean
  formErrors?: ApplicationException['errorInfo']['details']
}

export const initialTransformationProfiles: State = {
  transformationProfiles: [],
  selectedTransformationProfile: undefined,
  total: 0,
  loading: false,
  saving: false,
}

const { isLoadingAction, loadingReducer } = createLoadingReducer<State>(listTransformationProfiles)

const transformationProfileSlice = createSlice({
  name: 'transformationProfiles',
  initialState: initialTransformationProfiles,
  reducers: {
    clearTransformationProfile: (state): State => ({
      ...state,
      selectedTransformationProfile: undefined,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        listTransformationProfiles.fulfilled,
        (state, { payload }): State => ({
          ...state,
          transformationProfiles: payload.items,
          total: payload.total,
          loading: false,
        }),
      )
      .addCase(
        getTransformationProfile.fulfilled,
        (state, { payload: selectedTransformationProfile }): State => ({
          ...state,
          selectedTransformationProfile,
        }),
      )

      .addCase(createTransformationProfile.pending, (state): State => ({ ...state, saving: true }))
      .addCase(
        createTransformationProfile.rejected,
        (state, { payload }): State => ({ ...state, saving: false, formErrors: payload?.details }),
      )
      .addCase(
        createTransformationProfile.fulfilled,
        (state, { payload: profile }): State => ({
          ...state,
          transformationProfiles: [profile, ...state.transformationProfiles],
          total: state.total + 1,
          saving: false,
        }),
      )

      .addCase(updateTransformationProfile.pending, (state): State => ({ ...state, saving: true }))
      .addCase(
        updateTransformationProfile.rejected,
        (state, { payload }): State => ({ ...state, saving: false, formErrors: payload?.details }),
      )
      .addCase(
        updateTransformationProfile.fulfilled,
        (state, { payload: profile }): State => ({
          ...state,
          transformationProfiles: [profile, ...state.transformationProfiles.filter((p) => p.id !== profile.id)],
          selectedTransformationProfile:
            state.selectedTransformationProfile?.id === profile.id ? profile : state.selectedTransformationProfile,
          saving: false,
        }),
      )

      .addCase(
        deleteTransformationProfile.fulfilled,
        (state, { payload: { id } }): State => ({
          ...state,
          transformationProfiles: state.transformationProfiles.filter((profile) => profile.id !== id),
          total: state.total - 1,
          selectedTransformationProfile:
            state.selectedTransformationProfile?.id === id ? undefined : state.selectedTransformationProfile,
        }),
      )
      .addCase(clearTransformationProfileErrors, (state): State => ({ ...state, formErrors: undefined }))

      .addCase(logoutUser.fulfilled, (): State => initialTransformationProfiles)

      .addMatcher(isLoadingAction, loadingReducer)
  },
})

export const { clearTransformationProfile } = transformationProfileSlice.actions

export default transformationProfileSlice.reducer
